/*
 * @Author: Ryan
 * @Date: 2026-02-04
 * @Description: How to use section for Face Swap SEO page
 */
import Image from 'next/image';
import React from 'react';

interface StepData {
  number: number;
  title: string;
  description: string;
}

const steps: StepData[] = [
  {
    number: 1,
    title: 'Upload Your Video',
    description:
      'Upload video where you want to replace the face. For best results, make sure the face is clear and front-facing.',
  },
  {
    number: 2,
    title: 'Upload the Target Face',
    description:
      'Upload the face image you want to swap in. This can be your own photo or another face image.',
  },
  {
    number: 3,
    title: 'Click "Start"',
    description:
      'Our AI automatically detects faces and swaps them with high accuracy, preserving natural expressions and lighting.',
  },
  {
    number: 4,
    title: 'Export Your Video',
    description: 'Download the final result instantly or share it online in one click.',
  },
];

const StepItem: React.FC<StepData> = ({ number, title, description }) => (
  <div className="flex gap-4">
    <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-[#7C3AED] flex items-center justify-center text-white font-semibold text-sm">
      {number}
    </div>
    <div className="flex flex-col gap-2">
      <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
      <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
    </div>
  </div>
);

const HowToUseSection: React.FC = () => {
  return (
    <section className="w-full bg-white py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-10 pad:mb-14 sm:mb-16 leading-tight">
          How to Swap Faces Online with AI
        </h2>

        <div className="flex flex-col pad:flex-row gap-8 pad:gap-12 sm:gap-16 items-center">
          {/* Left image */}
          <div className="w-full pad:w-1/2">
            <div className="relative rounded-2xl overflow-hidden shadow-lg bg-[#F8F9FC]">
              <Image
                src="https://assets.falcocut.ai/face-swap/how-to-swap-faces-online-with-ai.webp"
                alt="How to swap faces online with AI - FalcoCut"
                width={560}
                height={480}
                className="w-full h-auto object-contain"
              />
            </div>
          </div>

          {/* Right steps */}
          <div className="w-full pad:w-1/2 flex flex-col gap-6 pad:gap-8">
            {steps.map((step) => (
              <StepItem key={step.number} {...step} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowToUseSection;
